import { Box, ConfirmationDialogBox, Divider } from '@atgfr8/atg-ui-tailwind';
import { CloseIcon } from '@atgfr8/icons';
import { deleteCollectionFlagAction } from '@store/collectionFlags/actions';
import { collectionFlagStore } from '@store/collectionFlags/store';
import type { CollectionFlag } from '@store/collectionFlags/types';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox/store';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { createSignal, For, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';

import { openCollectionFlagModal } from './CollectionFlagModal';
import {
  formatCollectionFlagDate,
  getCollectionFlagBannerStyle,
  getCollectionFlagLabel,
} from './constants';

const POPOVER_WIDTH = 448;
const REMOVE_COLLECTION_FLAG_DIALOG_ID = 'remove-collection-flag-dialog';

type CollectionFlagBannersProps = {
  class?: string;
  deleteSource: ChangeTrackingSource;
};

type CollectionFlagChipProps = {
  flag: CollectionFlag;
  onRemove: (flag: CollectionFlag) => void;
};

const CollectionFlagChip = (props: CollectionFlagChipProps) => {
  let wrapperRef: HTMLDivElement | undefined;
  const [showPopover, setShowPopover] = createSignal(false);
  const [alignRight, setAlignRight] = createSignal(false);

  const banner = () => getCollectionFlagBannerStyle(props.flag.flagType);
  const label = () => getCollectionFlagLabel(props.flag.flagType);
  const createdOn = () => formatCollectionFlagDate(props.flag.flagDate);
  const isDeleting = () => collectionFlagStore.deletingFlagId !== null;

  const openPopover = () => {
    if (!wrapperRef) return;
    const { left } = wrapperRef.getBoundingClientRect();
    setAlignRight(left + POPOVER_WIDTH > window.innerWidth);
    setShowPopover(true);
  };

  const handleFocusOut = (event: FocusEvent) => {
    if (wrapperRef?.contains(event.relatedTarget as Node | null)) return;
    setShowPopover(false);
  };

  const handleRemove = (event: MouseEvent) => {
    event.stopPropagation();
    setShowPopover(false);
    props.onRemove(props.flag);
  };

  return (
    <div
      ref={wrapperRef}
      class="relative min-w-0"
      onMouseEnter={openPopover}
      onMouseLeave={() => setShowPopover(false)}
      onFocusIn={openPopover}
      onFocusOut={handleFocusOut}
    >
      <div
        class={`flex h-[49px] cursor-pointer items-center gap-[6px] rounded-lg border px-2 ${banner().containerClass}`}
        data-testid={`collection-flag-chip-${props.flag.id}`}
        onClick={() => openCollectionFlagModal('edit', props.flag)}
      >
        <Dynamic
          component={banner().icon}
          size={24}
          class={`shrink-0 ${banner().iconClass}`}
        />
        <div class="flex min-w-0 flex-1 flex-col gap-[6px]">
          <button
            type="button"
            class={`cursor-pointer truncate text-left font-roboto text-[13px] font-bold ${banner().titleClass}`}
            aria-label={`Edit ${label()} flag`}
          >
            {label()}
          </button>
          <Show when={props.flag.description}>
            <span
              class={`truncate font-roboto text-[11px] ${banner().noteClass}`}
            >
              {props.flag.description}
            </span>
          </Show>
        </div>
        <Show when={props.flag.canDelete}>
          <button
            type="button"
            class="flex shrink-0 cursor-pointer items-center disabled:cursor-default disabled:opacity-50"
            aria-label={`Remove ${label()} flag`}
            disabled={isDeleting()}
            data-testid={`collection-flag-remove-${props.flag.id}`}
            onClick={handleRemove}
          >
            <CloseIcon size={16} class="text-[#B00020]" />
          </button>
        </Show>
      </div>
      <Show when={showPopover()}>
        <div
          class={`absolute top-full z-50 w-[448px] max-w-[90vw] pt-1 ${
            alignRight() ? 'right-0' : 'left-0'
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          <div class="rounded border border-[rgba(2,110,161,0.5)] bg-white px-2 shadow-md">
            <div class="flex h-[43px] items-center gap-[6px] px-4">
              <Dynamic
                component={banner().icon}
                size={24}
                class={`shrink-0 ${banner().iconClass}`}
              />
              <span
                class={`flex-1 truncate font-roboto text-base ${banner().titleClass}`}
              >
                {label()}
              </span>
              <button
                type="button"
                class="flex shrink-0 cursor-pointer items-center"
                aria-label="Close"
                onClick={() => setShowPopover(false)}
              >
                <CloseIcon size={24} class="text-[#263238]" />
              </button>
            </div>
            <div class="flex flex-col gap-2 px-4 py-2 font-roboto text-sm leading-5 tracking-[0.15px] text-[#000000DE]">
              <Show when={props.flag.description}>
                <div class="max-h-[124px] overflow-y-auto break-words">
                  <p class="font-bold">Description:</p>
                  <p>{props.flag.description}</p>
                </div>
                <Divider />
              </Show>
              <div class="flex gap-4 leading-[1.43] tracking-[0.17px]">
                <span class="whitespace-nowrap">{props.flag.flaggedBy}</span>
                <span class="whitespace-pre-wrap">{createdOn()}</span>
              </div>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};

export const CollectionFlagBanners = (props: CollectionFlagBannersProps) => {
  const [flagToRemove, setFlagToRemove] = createSignal<CollectionFlag | null>(
    null,
  );

  const activeFlags = () =>
    collectionFlagStore.flags.filter((flag) => flag.cleared !== true);

  const openRemoveDialog = (flag: CollectionFlag) => {
    setFlagToRemove(flag);
    openDialogBox(REMOVE_COLLECTION_FLAG_DIALOG_ID);
  };

  const closeRemoveDialog = () => {
    closeDialogBox(REMOVE_COLLECTION_FLAG_DIALOG_ID);
    setFlagToRemove(null);
  };

  const handleConfirmRemove = async () => {
    const flag = flagToRemove();
    closeRemoveDialog();
    if (!flag) return;
    await deleteCollectionFlagAction(flag.id, props.deleteSource);
  };

  return (
    <>
      <Show when={activeFlags().length > 0}>
        <Box
          class={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 ${props.class ?? ''}`}
        >
          <For each={activeFlags()}>
            {(flag) => (
              <CollectionFlagChip flag={flag} onRemove={openRemoveDialog} />
            )}
          </For>
        </Box>
      </Show>
      <ConfirmationDialogBox
        id={REMOVE_COLLECTION_FLAG_DIALOG_ID}
        dataTestId={REMOVE_COLLECTION_FLAG_DIALOG_ID}
        isDialogOpen={checkIfDialogBoxIsOpen(REMOVE_COLLECTION_FLAG_DIALOG_ID)}
        title={`Are you sure you want to remove the ${getCollectionFlagLabel(
          flagToRemove()?.flagType ?? '',
        )} flag?`}
        onSubmit={handleConfirmRemove}
        onSubmitText="Remove"
        confirmColor="error"
        closeDialogBox={closeRemoveDialog}
      />
    </>
  );
};
